import React, { useMemo, useState } from 'react';
import { DataGridPro, GridColDef, GridRenderCellParams, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { NewsItemModel } from '../../../../api-service/clients';
import { FasitDataGridToolbar, IFilterValues } from '../../../../components/data-grid/fasit-data-grid-toolbar';
import { ConfirmDialog } from '../../../../components/confirm-dialog/confirm-dialog';
import { formatDateTime } from '../../../../utilities/date-helper';
import { ForumGridOverlay } from '../forum-grid-overlay';
import { NewsItemIcons } from './news-item-icons';
import { NewsItemTool, newsItemTools } from './news-item-tools';
import { NewsItemDetailsDialog } from './news-item-details-dialog';
import { CreateUpdateNewsDialog } from './create-update-news-dialog';
import { NewsRecipientsDialog } from './news-recipients-dialog';
import { NewsStatisticsDialog } from './news-statistics-dialog';

export const forumNewsColumns: GridColDef[] = [
  {
    field: 'title',
    headerName: 'Titel',
    width: 250,
    renderCell: ({ row }: GridRenderCellParams<unknown, NewsItemModel>) => (
      <Box display="flex" alignItems="center" gap={1} overflow="hidden">
        <NewsItemIcons newsItem={row} />
        <Box component="span" overflow="hidden" textOverflow="ellipsis">
          {row.title}
        </Box>
      </Box>
    ),
  },
  {
    field: 'content',
    headerName: 'Indhold',
    flex: 1,
  },
  {
    field: 'author',
    headerName: 'Oprettet af',
    width: 180,
  },
  {
    field: 'creationDate',
    headerName: 'Oprettet',
    valueFormatter: ({ value }) => formatDateTime(value),
    width: 150,
  },
  {
    field: 'showFromDate',
    headerName: 'Synlig fra',
    valueFormatter: ({ value }) => formatDateTime(value),
    width: 150,
  },
  {
    field: 'expirationDate',
    headerName: 'Synlig til og med',
    valueFormatter: ({ value }) => (value ? formatDateTime(value) : ''),
    width: 160,
  },
  {
    field: 'modificationDate',
    headerName: 'Sidst ændret',
    valueFormatter: ({ value }) => (value ? formatDateTime(value) : ''),
    width: 150,
  },
  {
    field: 'showToAll',
    headerName: 'Vis til alle',
    width: 110,
    valueFormatter: ({ value }) => (value ? 'Ja' : 'Nej'),
  },
];

const filterValues = [
  { text: 'Vis synlige', value: 'visible' },
  { text: 'Vis alle', value: 'all' },
];

const defaultSortModel: GridSortModel = [{ field: 'showFromDate', sort: 'desc' }];

type ActiveDialog = { kind: 'create' } | { kind: 'details' | NewsItemTool; newsItem: NewsItemModel };

export interface IForumNewsTabComponentProps {
  newsItems: NewsItemModel[];
  isLoading: boolean;
  hasFailed: boolean;
  showExpired: boolean;
  canWrite: boolean;
  canShowToAll: boolean;
  canAttachFiles: boolean;
  onShowExpiredChange: (showExpired: boolean) => void;
  onNewsChanged: () => void;
  onCloseNewsItem: (newsItem: NewsItemModel) => void;
  onDeleteNewsItem: (newsItem: NewsItemModel) => void;
  authorFilterOptions: IFilterValues[];
  authorFilter: string;
  onAuthorFilterChange: (authorFilter: string) => void;
}

function NoNews() {
  return <ForumGridOverlay message="Der er ingen nyheder." />;
}

function FailedToLoadNews() {
  return <ForumGridOverlay message="Nyhederne kunne ikke hentes." />;
}

interface IForumNewsToolbarProps {
  activeFilter: string;
  onFilterChange: (event: SelectChangeEvent) => void;
  onCreateNewsItem?: () => void;
  authorPicker?: React.ReactNode;
}

// Declared out here so the grid keeps one toolbar instance: rebuilding it per render closes the open dropdown.
function ForumNewsToolbar(props: IForumNewsToolbarProps) {
  const { activeFilter, onFilterChange, onCreateNewsItem, authorPicker } = props;

  return FasitDataGridToolbar({
    title: 'Nyheder',
    filterValues,
    activeFilter,
    onFilterChange,
    onAddButtonClick: onCreateNewsItem,
    addButtonTooltip: 'Opret nyhed',
    customInputBeforeFilter: authorPicker,
  });
}

function dialogKindOf(kind: NewsItemTool, newsItem: NewsItemModel, canWrite: boolean) {
  const mayChange = canWrite && newsItem.canDelete;
  return kind === 'edit' && !mayChange ? 'details' : kind;
}

export function ForumNewsTabComponent(props: IForumNewsTabComponentProps) {
  const {
    newsItems,
    isLoading,
    hasFailed,
    showExpired,
    canWrite,
    canShowToAll,
    canAttachFiles,
    onShowExpiredChange,
    onNewsChanged,
    onCloseNewsItem,
    onDeleteNewsItem,
    authorFilterOptions,
    authorFilter,
    onAuthorFilterChange,
  } = props;

  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>();

  const columns = useMemo<GridColDef[]>(
    () => [
      ...forumNewsColumns,
      {
        field: 'tools',
        type: 'actions',
        headerName: 'Værktøjer',
        width: 180,
        getActions: ({ row }: GridRowParams<NewsItemModel>) =>
          newsItemTools(row, canWrite, (kind, newsItem) => setActiveDialog({ kind: dialogKindOf(kind, newsItem, canWrite), newsItem })),
      },
    ],
    [canWrite]
  );

  const components = useMemo(() => ({ Toolbar: ForumNewsToolbar, NoRowsOverlay: hasFailed ? FailedToLoadNews : NoNews }), [hasFailed]);

  const authorPicker = (
    <Select
      sx={{ height: '35px', marginRight: '5px' }}
      value={authorFilter}
      onChange={(event: SelectChangeEvent) => onAuthorFilterChange(event.target.value)}
      displayEmpty
      renderValue={selected => authorFilterOptions.find(option => option.value === selected)?.text}
      SelectDisplayProps={{ 'aria-label': 'Oprettet af' }}
    >
      {authorFilterOptions.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.text}
        </MenuItem>
      ))}
    </Select>
  );

  function handleFilterChange(event: SelectChangeEvent) {
    onShowExpiredChange(event.target.value === 'all');
  }

  function handleSortModelChange(model: GridSortModel) {
    setSortModel(model);
  }

  function handleRowClick(params: GridRowParams<NewsItemModel>) {
    setActiveDialog({ kind: 'details', newsItem: params.row });
  }

  function closeDialog() {
    setActiveDialog(undefined);
  }

  function handleNewsSaved() {
    closeDialog();
    onNewsChanged();
  }

  function confirmNewsItemAction(action: (newsItem: NewsItemModel) => void) {
    if (activeDialog && 'newsItem' in activeDialog) {
      action(activeDialog.newsItem);
    }
    closeDialog();
  }

  return (
    <>
      <DataGridPro<NewsItemModel>
        rows={newsItems}
        columns={columns}
        columnBuffer={columns.length}
        loading={isLoading}
        autoHeight
        density="compact"
        components={components}
        componentsProps={{
          toolbar: {
            activeFilter: showExpired ? 'all' : 'visible',
            onFilterChange: handleFilterChange,
            onCreateNewsItem: canWrite ? () => setActiveDialog({ kind: 'create' }) : undefined,
            authorPicker,
          },
        }}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
      />
      {activeDialog?.kind === 'details' && <NewsItemDetailsDialog newsItem={activeDialog.newsItem} onClose={closeDialog} />}
      {activeDialog?.kind === 'create' && (
        <CreateUpdateNewsDialog canShowToAll={canShowToAll} canAttachFiles={canAttachFiles} onSaved={handleNewsSaved} onCancel={closeDialog} />
      )}
      {activeDialog?.kind === 'edit' && (
        <CreateUpdateNewsDialog
          newsItemToUpdate={activeDialog.newsItem}
          canShowToAll={canShowToAll}
          canAttachFiles={canAttachFiles}
          onSaved={handleNewsSaved}
          onCancel={closeDialog}
        />
      )}
      {activeDialog?.kind === 'recipients' && <NewsRecipientsDialog newsItem={activeDialog.newsItem} canUpdate={canWrite} onClose={closeDialog} />}
      {activeDialog?.kind === 'statistics' && <NewsStatisticsDialog newsItem={activeDialog.newsItem} onClose={closeDialog} />}
      <ConfirmDialog
        open={activeDialog?.kind === 'close'}
        type="question"
        title="Er du sikker på, at du vil lukke denne nyhed?"
        message="Hvis du lukker nyheden vil slutdatoen blive sat til i går"
        confirmButtonText="Ja"
        cancelButtonText="Nej"
        onConfirmClick={() => confirmNewsItemAction(onCloseNewsItem)}
        onCancelClick={closeDialog}
      />
      <ConfirmDialog
        open={activeDialog?.kind === 'delete'}
        type="alert"
        title="Er du sikker på, at du vil slette denne nyhed?"
        message="Vær opmærksom på! Hvis du sletter nyheden kan handlingen ikke fortrydes."
        confirmButtonText="Ja"
        cancelButtonText="Nej"
        onConfirmClick={() => confirmNewsItemAction(onDeleteNewsItem)}
        onCancelClick={closeDialog}
      />
    </>
  );
}
